/**
 * Debounced server save for Workspace preferences: changes settle for `delayMs`
 * and only the latest value is sent. `flush` sends a pending change now (on
 * leaving the page), `cancel` drops it (on logout).
 */
export interface DebouncedSaver<T> {
  queue: (value: T) => void;
  flush: () => void;
  cancel: () => void;
}

export function createDebouncedSaver<T>(save: (value: T) => Promise<unknown>, delayMs = 1000): DebouncedSaver<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T } | null = null;

  const run = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!pending) return;
    const { value } = pending;
    pending = null;
    save(value).catch(() => undefined);
  };

  return {
    queue(value) {
      pending = { value };
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, delayMs);
    },
    flush: run,
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}
